import { FieldErrors, UseFormRegister, UseFormSetValue } from "react-hook-form";
import { FormField, PersonalProfileFormType } from "./fields";
import { useAppSelector } from "../../../store";
import { useParams } from "react-router";
import { useEffect } from "react";

type UserProfileFormItemProps = {
  fieldObj: FormField;
  register: UseFormRegister<PersonalProfileFormType>;
  errors: FieldErrors<PersonalProfileFormType>;
  setValue: UseFormSetValue<PersonalProfileFormType>;
  isEditMode: boolean;
};

export const InfoItem: React.FC<UserProfileFormItemProps> = ({
  fieldObj,
  register,
  isEditMode,
  setValue,
  errors,
}) => {
  const { id: userId } = useParams();
  const defaultValue = useAppSelector((state) =>
    fieldObj.selector(state, userId || "")
  );
  useEffect(() => {
    if (defaultValue) {
      setValue(fieldObj.name, defaultValue + "");
    }
  }, [defaultValue]);

  // Resolve display value for view mode
  const getDisplayValue = (): string => {
    const raw = defaultValue != null ? defaultValue + "" : "";
    if (!raw) return "—"; // em dash for empty values

    // For radio/select fields, show the label instead of raw value
    if (fieldObj.type === "radio" && fieldObj.fields) {
      const match = fieldObj.fields.find((f) => f.value === raw);
      if (match && match.label) return match.label;
    }

    // Append unit if present
    if (fieldObj.rightAddon && raw !== "—") return `${raw} ${fieldObj.rightAddon}`;

    return raw;
  };

  // ── VIEW MODE ──────────────────────────────────────────────────────────
  if (!isEditMode) {
    const displayValue = getDisplayValue();
    const isEmpty = displayValue === "—";

    // Status field: render as colored chip
    if (fieldObj.name === "status") {
      const isActive = !defaultValue || (defaultValue + "").toUpperCase() !== "DISABLED";
      return (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {fieldObj.label}
          </span>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold w-fit ${
              isActive
                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                isActive ? "bg-green-500" : "bg-red-500"
              }`}
            />
            {isActive ? "Attivo" : "Disabilitato"}
          </span>
        </div>
      );
    }

    // Textarea fields: render as preformatted text
    if (fieldObj.type === "textarea") {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            {fieldObj.label}
          </span>
          <p
            className={`text-sm whitespace-pre-line leading-relaxed ${
              isEmpty
                ? "text-gray-300 dark:text-gray-600 italic"
                : "text-gray-800 dark:text-gray-200"
            }`}
          >
            {displayValue}
          </p>
        </div>
      );
    }

    // Default: label + value
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          {fieldObj.label}
        </span>
        <span
          className={`text-sm font-medium ${
            isEmpty
              ? "text-gray-300 dark:text-gray-600"
              : "text-gray-900 dark:text-white"
          }`}
        >
          {displayValue}
        </span>
      </div>
    );
  }

  // ── EDIT MODE ──────────────────────────────────────────────────────────

  const errorMsg = errors[fieldObj.name]?.message;

  // Radio/select fields
  if (fieldObj.type === "radio") {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {fieldObj.label}
        </label>
        <select
          id={fieldObj.name}
          disabled={fieldObj.disabled}
          {...register(fieldObj.name, {
            ...fieldObj.rules,
            required: false,
          })}
          className="py-2 px-3 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full"
        >
          {fieldObj.fields?.map((field, i) => (
            <option key={field.value + "" + i} value={field.value}>
              {field.label || "—"}
            </option>
          ))}
        </select>
        {errorMsg && <span className="text-red-500 text-xs">{errorMsg}</span>}
      </div>
    );
  }

  // Textarea fields
  if (fieldObj.type === "textarea") {
    return (
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {fieldObj.label}
        </label>
        <textarea
          placeholder={fieldObj.placeholder}
          disabled={fieldObj.disabled}
          {...register(fieldObj.name, {
            ...fieldObj.rules,
            required: false,
          })}
          className="py-2 px-3 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full h-32 resize-y"
        />
        {errorMsg && <span className="text-red-500 text-xs">{errorMsg}</span>}
      </div>
    );
  }

  // Text/email/number inputs
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        {fieldObj.label}
      </label>
      <input
        placeholder={fieldObj.placeholder}
        type={fieldObj.type}
        disabled={fieldObj.disabled}
        {...register(fieldObj.name, {
          ...fieldObj.rules,
          required: false,
        })}
        className="py-2 px-3 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 w-full"
      />
      {errorMsg && <span className="text-red-500 text-xs">{errorMsg}</span>}
    </div>
  );
};
